import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Wind, 
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  Home,
  Building,
  Factory,
  AlertTriangle,
  Snowflake,
  Flame,
  Star,
  Users,
  Mail,
  Thermometer,
  Settings
} from "lucide-react";

// Import HVAC images
import hvacImage from "@/assets/hvac-ontario.jpg";
import hvacInstallation from "@/assets/hvac-installation.jpg";
import hvacFurnaceService from "@/assets/hvac-furnace-service.jpg";
import commercialHvac from "@/assets/commercial-hvac.jpg";
import emergencyHvacRepair from "@/assets/emergency-hvac-repair.jpg";

const HVACServices = () => {
  const faqData = [
    {
      question: "Do you provide HVAC installation services in Toronto?",
      answer: "Yes. Blue Knight Contracting provides complete HVAC installation services throughout Toronto and the GTA, including heating, cooling, and ventilation systems for residential and commercial properties."
    },
    {
      question: "Are your HVAC technicians licensed in Ontario?",
      answer: "Absolutely. All our HVAC technicians are fully licensed and certified in Ontario with extensive experience in heating, cooling, and ventilation system installation, repair, and maintenance."
    },
    {
      question: "Do you offer emergency HVAC repair services?",
      answer: "Yes. We provide 24/7 emergency HVAC services for urgent heating and cooling system failures, ensuring your comfort is restored quickly and efficiently."
    },
    {
      question: "Can you handle commercial HVAC projects?",
      answer: "Yes. Our experienced HVAC team handles commercial projects including office buildings, retail spaces, restaurants, warehouses, and industrial HVAC systems across Ontario."
    },
    {
      question: "Do you provide HVAC maintenance services?",
      answer: "Yes. We offer comprehensive HVAC maintenance programs including seasonal tune-ups, filter changes, duct cleaning, and preventive maintenance to keep your systems running efficiently."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "HVAC Services Ontario",
    description: "Professional HVAC services across Ontario including heating, cooling, ventilation installation, repair, and maintenance for residential and commercial properties.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "HVAC Services",
    areaServed: "Ontario, Canada"
  };

  const hvacServices = [
    {
      title: "Heating Systems",
      description: "Complete heating system installation, repair, and maintenance for optimal winter comfort.",
      icon: Flame,
      services: [
        "Furnace installation and repair",
        "Boiler systems",
        "Heat pump installation",
        "Radiant heating systems",
        "Gas line connections",
        "Heating system maintenance"
      ]
    },
    {
      title: "Air Conditioning",
      description: "Professional cooling system services for comfortable summers and year-round climate control.",
      icon: Snowflake,
      services: [
        "Central air conditioning",
        "Ductless mini-split systems",
        "Heat pump cooling",
        "AC repair and maintenance",
        "Energy-efficient upgrades",
        "Refrigerant services"
      ]
    },
    {
      title: "Ventilation Systems",
      description: "Indoor air quality solutions and ventilation systems for healthy, comfortable environments.",
      icon: Wind,
      services: [
        "Ductwork design and installation",
        "Exhaust fan installation",
        "Air purification systems",
        "Heat recovery ventilators",
        "Bathroom and kitchen ventilation",
        "Indoor air quality testing"
      ]
    },
    {
      title: "Commercial HVAC",
      description: "Large-scale HVAC solutions for offices, retail, restaurants, and industrial facilities.",
      icon: Building,
      services: [
        "Commercial heating systems",
        "Rooftop HVAC units",
        "Building automation systems",
        "Industrial ventilation",
        "Energy management systems",
        "Preventive maintenance programs"
      ]
    },
    {
      title: "Emergency Services",
      description: "24/7 emergency HVAC repairs for urgent heating and cooling system failures.",
      icon: AlertTriangle,
      services: [
        "Emergency furnace repair",
        "AC breakdown service",
        "No heat emergency calls",
        "System diagnostic services",
        "Urgent parts replacement",
        "After-hours service calls"
      ]
    },
    {
      title: "Energy Efficiency",
      description: "Energy-efficient HVAC solutions and upgrades to reduce costs and environmental impact.",
      icon: Settings,
      services: [
        "High-efficiency system upgrades",
        "Smart thermostat installation",
        "Energy audits and assessments",
        "Duct sealing and insulation",
        "Programmable controls",
        "Green HVAC solutions"
      ]
    }
  ];

  const serviceAreas = [
    "Greater Toronto Area",
    "Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington", "Milton",
    "Niagara Region", 
    "Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold",
    "Eastern Ontario",
    "Ottawa", "Kingston", "Cornwall", "Belleville", "Pembroke", "Petawawa",
    "Southwestern Ontario", 
    "London", "Windsor", "Kitchener", "Waterloo", "Cambridge", "Guelph", "Sarnia", "Chatham",
    "Northern Ontario",
    "Sudbury", "Thunder Bay", "North Bay", "Sault Ste. Marie", "Timmins", "Kenora"
  ];

  return (
    <>
      <SEOMeta 
        title="HVAC Services — Heating, Cooling & Ventilation | Niagara • Hamilton • GTA"
        description="Full-service HVAC contractors for furnaces, AC and ventilation in Niagara, Hamilton & the GTA. Installation, repair and preventative maintenance."
        keywords="HVAC contractor Niagara, Hamilton HVAC service, GTA heating and cooling, furnace install, AC repair, duct cleaning, HVAC maintenance"
        canonicalUrl="https://blueknightcontracting.ca/services/hvac"
      />
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Expert HVAC Services in Ontario
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides professional HVAC services across Ontario with licensed, experienced technicians. From heating and cooling installation to emergency repairs and maintenance, we keep your home and business comfortable year-round.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Phone className="h-5 w-5 mr-2" />
                    24/7 Emergency Service
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={hvacInstallation} 
                  alt="Professional HVAC technician installing air conditioning system in Ontario home"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Comprehensive HVAC Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our licensed HVAC technicians provide complete heating, ventilation, and air conditioning services for optimal comfort and energy efficiency.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {hvacServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured HVAC Projects
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our portfolio of HVAC installations and services across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={hvacImage} 
                    alt="Ontario HVAC contractor installing commercial air system"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">HVAC Installation</h3>
                  <p className="text-muted-foreground">
                    Complete heating and cooling system installations
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={hvacFurnaceService} 
                    alt="Ontario HVAC technician servicing furnace and heating system"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Heating Services</h3>
                  <p className="text-muted-foreground">
                    Furnace installation, repair, and maintenance
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={commercialHvac} 
                    alt="Commercial HVAC installation with industrial air handling units in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Commercial HVAC</h3>
                  <p className="text-muted-foreground">
                    Large-scale commercial and industrial systems
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={emergencyHvacRepair} 
                    alt="Emergency HVAC repair work by Ontario technician"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Emergency Services</h3>
                  <p className="text-muted-foreground">
                    24/7 emergency heating and cooling repairs
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency Services CTA */}
        <section className="py-16 bg-destructive text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <AlertTriangle className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                HVAC Emergency? Call Now!
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Furnace not working? AC breakdown in summer heat? Don't suffer - call our emergency HVAC technicians for immediate assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" variant="secondary">
                  <Phone className="h-5 w-5 mr-2" />
                  Call Emergency Line
                </Button>
                <div className="flex items-center gap-2 text-white/90">
                  <Clock className="h-5 w-5" />
                  <span>Available 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight HVAC?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Licensed & Insured</h3>
                <p className="text-muted-foreground">
                  Fully licensed Ontario HVAC technicians with comprehensive insurance
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Guarantee</h3>
                <p className="text-muted-foreground">
                  Warranty on all HVAC work and premium equipment
                </p>
              </Card>

              <Card className="text-center p-6">
                <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">24/7 Emergency</h3>
                <p className="text-muted-foreground">
                  Round-the-clock emergency HVAC services
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Energy Efficient</h3>
                <p className="text-muted-foreground">
                  High-efficiency systems to reduce energy costs
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                HVAC Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Blue Knight Contracting provides professional HVAC services throughout Ontario, including the GTA and Niagara region
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => {
                const isRegionHeader = area.includes("Ontario") || area.includes("Region") || area.includes("Area");
                
                return (
                  <Card key={index} className={`p-4 text-center ${isRegionHeader ? 'bg-primary text-white col-span-full sm:col-span-2 lg:col-span-4' : ''}`}>
                    <MapPin className={`h-6 w-6 mx-auto mb-2 ${isRegionHeader ? 'text-white' : 'text-primary'}`} />
                    <p className={`font-medium ${isRegionHeader ? 'text-white text-lg' : ''}`}>{area}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our HVAC services in Ontario
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Need Professional HVAC Services?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for expert HVAC services across Ontario. Stay comfortable year-round with our reliable heating and cooling solutions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/contact">Get Free HVAC Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                <Phone className="h-5 w-5 mr-2" />
                Emergency Service
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default HVACServices;